import './constants/travel.css';
import { useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Control,
  FieldArrayWithId,
  FormState,
  UseFieldArrayRemove,
  UseFormReturn,
  useFieldArray,
} from 'react-hook-form';

import { SelectInput, ButtonWizard } from '../../components';
import { SideSheet, SideSheetHeader } from '../../layouts';
import { Bars2Icon, TrashIcon } from '@heroicons/react/24/outline';

interface ApprovalHierarchyItem {
  id: number;
  approvalLevel: number;
  approverId: number;
}

export interface ApprovalHierarchyFormValues {
  approvers: Array<{ approverId: number | null }>;
}

export interface ApprovalHierarchyMappingInput {
  approverId: number;
}

interface EmployeeDropdownOption {
  label: string;
  value: string | number;
}

const hasSelectedApprover = (item: {
  approverId: number | null;
}): item is { approverId: number } =>
  Number.isInteger(item.approverId) && Number(item.approverId) > 0;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  approvalConfigForm: UseFormReturn<ApprovalHierarchyFormValues>;
  employeeDropdown: EmployeeDropdownOption[];
  claimApprovalHierarchy?: ApprovalHierarchyItem[];
  onSubmit?: (
    payload: ApprovalHierarchyMappingInput[],
  ) => void | Promise<void>;
  isSubmitting?: boolean;
}

interface DraggableRowProps {
  field: FieldArrayWithId<
    ApprovalHierarchyFormValues,
    'approvers',
    'id'
  >;
  index: number;
  control: Control<ApprovalHierarchyFormValues>;
  formState: FormState<ApprovalHierarchyFormValues>;
  employeeDropdown: EmployeeDropdownOption[];
  remove: UseFieldArrayRemove;
  isSubmitting: boolean;
}

const DraggableRow = ({
  field,
  index,
  control,
  formState,
  employeeDropdown,
  remove,
  isSubmitting,
}: DraggableRowProps) => {
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id, disabled: isSubmitting });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <tr ref={setNodeRef} style={style} className="border-b border-gray-100">
      {/* Drag handle */}
      <td className="px-3 py-2">
        <button
          type="button"
          disabled={isSubmitting}
          {...attributes}
          {...listeners}
          className="cursor-grab rounded p-1 active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
          aria-label={`Move approval level ${index + 1}`}
        >
          <Bars2Icon className="h-4 w-4 text-primary-600 stroke-[2.5px]" />
        </button>
      </td>

      {/* Hierarchy number */}
      <td className="px-3 py-2 text-sm font-medium">{index + 1}</td>

      {/* Approver dropdown */}
      <td className="px-3 py-2">
        <SelectInput
          name={`approvers.${index}.approverId`}
          control={control}
          formState={formState}
          rules={{ required: '* Required' }}
          config={{
            showSearch: true,
            placeholder: 'Choose approver',
            optionFilterProp: 'label',
            optionLabelProp: 'label',
            options: employeeDropdown,
            disabled: isSubmitting,
          }}
          addOnClass="w-full"
        />
      </td>

      {/* Delete */}
      <td className="px-3 py-2">
        <button
          type="button"
          disabled={isSubmitting}
          onClick={() => remove(index)}
          className="cursor-pointer rounded p-1 disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
          aria-label={`Remove approval level ${index + 1}`}
        >
          <TrashIcon className="h-4 w-4 text-error-400" />
        </button>
      </td>
    </tr>
  );
};

const ApprovalHierarchySideSheet = ({
  isOpen,
  onClose,
  approvalConfigForm,
  employeeDropdown,
  claimApprovalHierarchy = [],
  onSubmit,
  isSubmitting = false,
}: Props) => {
  const { control, formState } = approvalConfigForm;

  const { fields, move, append, remove, replace } = useFieldArray({
    control,
    name: 'approvers',
  });

  useEffect(() => {
    if (!isOpen) return;

    if (claimApprovalHierarchy?.length) {
      const sorted = [...claimApprovalHierarchy].sort(
        (a, b) => a.approvalLevel - b.approvalLevel,
      );

      replace(
        sorted.map(item => ({
          approverId: item.approverId,
        })),
      );
    } else {
      replace([]);
    }
  }, [claimApprovalHierarchy, isOpen, replace]);

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (isSubmitting) return;
    if (!over || active.id === over.id) return;

    const oldIndex = fields.findIndex(f => f.id === active.id);
    const newIndex = fields.findIndex(f => f.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      move(oldIndex, newIndex); // ✅ IMMEDIATE & CORRECT
    }
  };

  const handleAdd = () => {
    if (isSubmitting) return;
    append({ approverId: null });
  };

  const handleSubmit = approvalConfigForm.handleSubmit(async values => {
    if (isSubmitting) return;
    if (!values.approvers.length) return;
    const payload = values.approvers.filter(hasSelectedApprover).map(item => ({
      approverId: item.approverId,
    }));
    if (payload.length !== values.approvers.length) return;
    await onSubmit?.(payload);
  });

  return (
    <AnimatePresence>
      {isOpen && (
        <SideSheet
          isOpen={isOpen}
          addOnClass="w-full max-w-none md:!w-[50vw]"
          onClose={() => {
            if (!isSubmitting) onClose();
          }}
          headerChildren={
            <div className="border-b border-gray-100">
              <SideSheetHeader
                title="Rearrange Approval Hierarchy"
                onCloseClick={() => {
                  if (!isSubmitting) onClose();
                }}
              />
            </div>
          }
          contentChildren={
            <div className="p-4" aria-busy={isSubmitting}>
              <div className="overflow-x-auto outline outline-[1.5px] outline-gray-100 rounded">
                <DndContext
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={fields.map(f => f.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2">Drag</th>
                          <th className="px-3 py-2">Level</th>
                          <th className="px-3 py-2">Approver</th>
                          <th className="px-3 py-2">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fields.map((field, index) => (
                          <DraggableRow
                            key={field.id}
                            field={field}
                            index={index}
                            control={control}
                            formState={formState}
                            employeeDropdown={employeeDropdown}
                            remove={remove}
                            isSubmitting={isSubmitting}
                          />
                        ))}
                      </tbody>
                    </table>
                  </SortableContext>
                </DndContext>

                {fields.length === 0 && (
                  <div className="min-h-[200px] border-2 border-dotted border-primary-900 grid place-items-center">
                    <span className="text-xs text-text-500">No data found</span>
                  </div>
                )}
              </div>

              <button
                type="button"
                disabled={isSubmitting}
                onClick={handleAdd}
                className="mt-3 rounded text-primary-600 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
              >
                + Add Approver
              </button>
            </div>
          }
          footerChildren={
            <div className="p-4 border-t flex gap-4">
              <ButtonWizard
                addOnClass="bg-primary-600"
                buttonText="Update"
                loading={isSubmitting}
                disabled={isSubmitting || fields.length === 0}
                onBtnClick={handleSubmit}
              />
              <ButtonWizard
                addOnClass="bg-white border border-primary-600"
                addOnTextClass="text-primary-600"
                buttonText="Cancel"
                disabled={isSubmitting}
                onBtnClick={onClose}
              />
            </div>
          }
        />
      )}
    </AnimatePresence>
  );
};

export default ApprovalHierarchySideSheet;
